import {
    type B2bCatalog,
    type B2bOrderDetail,
    getB2bOrderFileUrl,
    issueB2bPi,
    type OrderLineInput,
    type OrderQuote,
    previewB2bPi,
    quoteB2bOrder,
    reviewB2bOrder,
    type ReviewLineInput,
    type SchemeEntitlement,
} from '@api/b2b';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { ActionIcon, Button, Checkbox, NumberInput, Select, Textarea, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './b2b.module.css';
import SendPiModal from './SendPiModal';
import { freeColumn } from './freeColumn';
import { Card, Pill, ProductCell, SchemeStrip } from './shared';
import { useFreePickDefaults } from './useFreePickDefaults';
import { EDITABLE_STATUSES, errorMessage, formatDay, formatMoment, freeEligibility, money, openSignedFile, units } from './utils';



interface EditLine extends ReviewLineInput {
    key: string;
}

/** The PI has no page margin of its own (the PDF printer adds it), so the on-screen preview gets one. */
const withPreviewMargin = (html: string) => html.replace('</head>', '<style>body{padding:24px 28px!important}</style></head>');

interface ReviewRow {
    key: string;
    productId: string;
    name: string;
    sku: string;
    listPrice: number | null;
    onHand: number | null;
    available: number | null;
    line: EditLine;
    /** A product a scheme lets be taken free without being on the order. */
    freeOnly: boolean;
}

interface LockedRow {
    key: string;
    description: string;
    quantity: number;
    unitPrice: number | null;
    lineTotal: number | null;
    note: string;
}

/**
 * A locked order (payment done and after): exactly what was charged, read from the order itself,
 * never re-priced against today's schemes. A line whose price the brand changed in review shows
 * the reason recorded then.
 */
const LockedLines: React.FC<{ detail: B2bOrderDetail }> = ({ detail }) => {
    const { order } = detail;
    // The latest price change per product, with its reason, from the review records.
    const reasons = new Map<string, string>();
    for (const entry of detail.audit.filter((a) => a.kind === 'REVIEWED')) {
        for (const c of entry.changes ?? []) {
            if (c.change === 'CHANGED' && c.reason && c.before?.unitPrice !== c.after?.unitPrice) {
                reasons.set(c.productId, `Price changed from ${money(c.before?.unitPrice ?? 0)}: ${c.reason}`);
            }
        }
    }
    const rows: LockedRow[] = order.items.map((i) => {
        const free = i.lineItemId.startsWith('FREE-');
        return {
            key: `${i.itemId}:${i.lineItemId}`,
            description: `${i.description ?? i.itemId}${free ? ' (free)' : ''}`,
            quantity: Number(i.quantity),
            unitPrice: i.unitPrice,
            lineTotal: i.lineTotal,
            note: free ? 'Free piece, netted by a scheme discount' : (reasons.get(i.itemId) ?? ''),
        };
    });
    const schemes = (order.additionalCharges ?? []).filter((c) => c.amount < 0).map((c) => c.label.replace(/^Discount - /, ''));
    const columns: DataSheetColumn<LockedRow>[] = [
        { key: 'description', header: 'Product', width: 320 },
        { key: 'quantity', header: 'Quantity', width: 100, align: 'right', render: (v: number) => units(v) },
        { key: 'unitPrice', header: 'Price', width: 120, align: 'right', render: (v: number | null) => money(v) },
        { key: 'lineTotal', header: 'Value', width: 130, align: 'right', render: (v: number | null) => money(v) },
        ...(rows.some((r) => r.note) ? [{ key: 'note', header: 'Note', width: 280 } satisfies DataSheetColumn<LockedRow>] : []),
    ];
    return (
        <>
            <span className={styles.muted}>This order is {order.status}; its lines are locked. This is what was charged.</span>
            <DataSheet<LockedRow> bare hideHeaderBar data={rows} columns={columns} getRowKey={(r) => r.key} maxHeight="auto" />
            {schemes.length ? (
                <div className={styles.row}>
                    <span className={styles.cardHint}>Schemes applied:</span>
                    {schemes.map((label) => (
                        <Pill key={label} label={label} tone="positive" />
                    ))}
                </div>
            ) : null}
        </>
    );
};

/** The latest PI revision on file: its number and S3 key, from the order's files. */
const latestPi = (files: string[]): { revision: number; key: string } | null =>
    files.reduce<{ revision: number; key: string } | null>((best, key) => {
        const m = /\/pi-rev(\d+)\.pdf$/.exec(key);
        const revision = m ? Number(m[1]) : 0;
        return revision > (best?.revision ?? 0) ? { revision, key } : best;
    }, null);

/**
 * Review and issue the proforma invoice, in one place:
 * 1. Check the order: quantities, prices (a change needs a reason), free pieces, with stock at
 *    the ship-from warehouse beside each line.
 * 2. The PI exactly as the customer will get it, rendered by the server from the lines on screen
 *    (saved or not), with who it is emailed to; one button saves any changes and issues it.
 * Once issued, a banner says when, to whom and until when, with the PDF. Changing the lines of an
 * issued PI sends the order back to Submitted, and the button issues the next revision.
 */
const ReviewTab: React.FC<{ detail: B2bOrderDetail; catalog: B2bCatalog | null; isAdmin: boolean; customerName: string; onSaved: () => void }> = ({
    detail,
    catalog,
    isAdmin,
    customerName,
    onSaved,
}) => {
    const { order } = detail;
    const editable = EDITABLE_STATUSES.has(order.status);
    const issued = latestPi(detail.files);
    const initialPaid = useMemo<EditLine[]>(() => {
        const byProduct = new Map<string, EditLine>();
        for (const item of order.items.filter((i) => !i.lineItemId.startsWith('FREE-'))) {
            const prior = byProduct.get(item.itemId);
            byProduct.set(item.itemId, {
                key: item.itemId,
                productId: item.itemId,
                quantity: (prior?.quantity ?? 0) + Number(item.quantity),
                unitPrice: item.unitPrice ?? undefined,
            });
        }
        return [...byProduct.values()];
    }, [order.items]);
    const initialFree = useMemo<OrderLineInput[]>(
        () => order.items.filter((i) => i.lineItemId.startsWith('FREE-')).map((i) => ({ productId: i.itemId, quantity: Number(i.quantity) })),
        [order.items],
    );

    const [lines, setLines] = useState<EditLine[]>(initialPaid);
    const [free, setFree] = useState<OrderLineInput[]>(initialFree);
    const [manualFree, setManualFree] = useState(false);
    const [freeNote, setFreeNote] = useState('');
    const [addProduct, setAddProduct] = useState<string | null>(null);
    const [quote, setQuote] = useState<OrderQuote | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const savedTerms = order.termsAndConditions ?? '';
    const [terms, setTerms] = useState(savedTerms);

    useEffect(() => {
        setLines(initialPaid);
        setFree(initialFree);
    }, [initialPaid, initialFree]);
    useEffect(() => setTerms(savedTerms), [savedTerms]);

    const payload = useMemo(() => {
        const paid = lines.filter((l) => l.quantity > 0).map(({ key: _k, ...rest }) => rest);
        const picks = free.filter((f) => f.productId && f.quantity > 0);
        return { paid, picks };
    }, [lines, free]);
    const [debounced] = useDebouncedValue(JSON.stringify({ ...payload, manualFree, freeNote }), 400);

    useEffect(() => {
        // A locked order shows what was charged; it is never re-priced against today's schemes.
        if (!editable) return;
        const p = JSON.parse(debounced) as { paid: ReviewLineInput[]; picks: OrderLineInput[]; manualFree: boolean; freeNote: string };
        if (p.paid.length === 0) {
            setQuote(null);
            return;
        }
        let stale = false;
        quoteB2bOrder(order.b2bCustomerId, p.paid, p.manualFree ? undefined : p.picks, p.manualFree ? { lines: p.picks, note: p.freeNote || 'preview' } : undefined)
            .then((q) => !stale && (setQuote(q), setError(null)))
            .catch((e) => !stale && setError(errorMessage(e)));
        return () => {
            stale = true;
        };
    }, [debounced, order.b2bCustomerId, editable]);

    // Unsaved changes: the preview renders them, and issuing saves them first.
    const initialPayload = useMemo(
        () => JSON.stringify({ paid: initialPaid.map(({ key: _k, ...rest }) => rest), picks: initialFree }),
        [initialPaid, initialFree],
    );
    const termsDirty = terms.trim() !== savedTerms.trim();
    const dirty = editable && (manualFree || termsDirty || JSON.stringify(payload) !== initialPayload);
    const draft = useMemo(
        () =>
            dirty
                ? {
                      lines: payload.paid,
                      ...(manualFree ? { freeOverride: { lines: payload.picks, note: freeNote || 'preview' } } : { freePicks: payload.picks }),
                      ...(termsDirty ? { termsAndConditions: terms } : {}),
                  }
                : undefined,
        [dirty, payload, manualFree, freeNote, termsDirty, terms],
    );
    const [debouncedDraft] = useDebouncedValue(JSON.stringify(draft ?? null), 500);
    const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewB2bPi>> | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    useEffect(() => {
        if (!editable) return;
        let stale = false;
        const parsed = JSON.parse(debouncedDraft) as Parameters<typeof previewB2bPi>[1] | null;
        if (parsed && parsed.lines.length === 0) {
            setPreview(null);
            return;
        }
        previewB2bPi(order.purchaseOrderId, parsed ?? undefined)
            .then((p) => !stale && (setPreview(p), setPreviewError(null)))
            .catch((e) => !stale && setPreviewError(errorMessage(e)));
        return () => {
            stale = true;
        };
        // order.items: the saved lines changed (after a save or an issue), so re-render the saved PI.
    }, [debouncedDraft, editable, order.purchaseOrderId, order.items]);

    // A saved order opens with the free pieces it was saved with; later changes to what it earns re-fill them.
    useFreePickDefaults(quote, free, setFree, { enabled: !manualFree, keepFirst: true });

    const product = useMemo(() => new Map((catalog?.products ?? []).map((p) => [p.productId, p])), [catalog]);
    const quoted = useMemo(() => new Map((quote?.lines ?? []).map((l) => [l.productId, l])), [quote]);
    const stock = useMemo(() => new Map(detail.availability.map((a) => [a.productId, a])), [detail.availability]);

    const entitlements = quote?.schemes.entitlements ?? [];
    const eligible = manualFree ? new Map<string, SchemeEntitlement>() : freeEligibility(entitlements, quote?.lines ?? []);
    // A scheme with a list of free products can give one that is not on the order: it gets a row of its own.
    const freeOnly: EditLine[] = [...eligible.keys()].filter((id) => !lines.some((l) => l.productId === id)).map((id) => ({ key: `free:${id}`, productId: id, quantity: 0 }));

    const rows: ReviewRow[] = [...lines, ...freeOnly].map((line) => {
        const p = product.get(line.productId);
        const q = quoted.get(line.productId);
        const s = stock.get(line.productId);
        return {
            key: line.key,
            productId: line.productId,
            name: p?.name ?? q?.name ?? line.productId,
            sku: p?.sku ?? q?.sku ?? line.productId,
            listPrice: q?.listPrice ?? p?.unitPrice ?? null,
            onHand: s?.onHand ?? null,
            available: s?.available ?? null,
            line,
            freeOnly: line.key.startsWith('free:'),
        };
    });

    const update = (key: string, patch: Partial<EditLine>) => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));

    const columns: DataSheetColumn<ReviewRow>[] = [
        { key: 'name', header: 'Product', width: 260, sticky: true, render: (_v, r) => <ProductCell name={r.name} sku={r.sku} /> },
        {
            key: 'available',
            header: 'Available',
            width: 110,
            align: 'right',
            render: (_v, r) =>
                r.available === null ? (
                    '—'
                ) : (
                    <span className={r.available < r.line.quantity ? styles.error : undefined}>
                        {units(r.available)}
                        {r.available < r.line.quantity ? ' (short)' : ''}
                    </span>
                ),
        },
        {
            key: 'quantity',
            header: 'Quantity',
            width: 120,
            render: (_v, r) =>
                r.freeOnly ? (
                    <span className={styles.muted}>free only</span>
                ) : (
                    <NumberInput size="xs" min={0} allowDecimal={false} disabled={!editable} className={styles.qtyInput} value={r.line.quantity} onChange={(v) => update(r.key, { quantity: Number(v) || 0 })} />
                ),
        },
        ...(manualFree ? [] : [freeColumn<ReviewRow>(eligible, free.filter((f) => f.productId), setFree)]),
        { key: 'listPrice', header: 'List price', width: 100, align: 'right', render: (v: number | null) => money(v) },
        {
            key: 'price',
            header: 'Price',
            width: 130,
            render: (_v, r) => (
                <NumberInput
                    size="xs"
                    min={0}
                    decimalScale={2}
                    disabled={!editable}
                    className={styles.priceInput}
                    value={r.line.unitPrice ?? r.listPrice ?? ''}
                    onChange={(v) => update(r.key, { unitPrice: v === '' ? undefined : Number(v) })}
                />
            ),
        },
        {
            key: 'reason',
            header: 'Reason for price change',
            width: 220,
            render: (_v, r) =>
                r.line.unitPrice !== undefined && r.listPrice !== null && r.line.unitPrice !== r.listPrice ? (
                    <TextInput size="xs" disabled={!editable} placeholder="Required" value={r.line.priceReason ?? ''} onChange={(e) => update(r.key, { priceReason: e.currentTarget.value })} />
                ) : (
                    ''
                ),
        },
        { key: 'value', header: 'Value', width: 110, align: 'right', render: (_v, r) => money(quoted.get(r.productId)?.lineTotal) },
        {
            key: 'remove',
            header: '',
            width: 48,
            render: (_v, r) =>
                editable ? (
                    <ActionIcon variant="subtle" color="gray" aria-label="Remove line" onClick={() => setLines((all) => all.filter((l) => l.key !== r.key))}>
                        <TrashIcon size={16} />
                    </ActionIcon>
                ) : null,
        },
    ];

    const addOptions = (catalog?.products ?? [])
        .filter((p) => !lines.some((l) => l.productId === p.productId))
        .map((p) => ({ value: p.productId, label: `${p.name} (${p.sku})` }));
    const earned = entitlements.reduce((sum, e) => sum + e.units, 0);

    const saveLines = () =>
        reviewB2bOrder(order.purchaseOrderId, {
            lines: payload.paid,
            ...(manualFree ? { freeOverride: { lines: payload.picks, note: freeNote } } : { freePicks: payload.picks }),
            ...(termsDirty ? { termsAndConditions: terms } : {}),
        });

    const act = async (action: () => Promise<unknown>) => {
        setSaving(true);
        setError(null);
        try {
            await action();
            onSaved();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const blocked = !!quote && quote.errors.length > 0;
    const nextRevision = (issued?.revision ?? 0) + 1;
    const issueLabel = issued ? `Send revised PI (Rev ${nextRevision})` : 'Send PI to customer';
    // An issued PI with no changes on screen: nothing to issue until the lines change.
    const canIssue = order.status === 'Submitted' || dirty;

    const issuedBanner = issued ? (
        <div className={styles.issuedBanner}>
            <div className={styles.stack}>
                <span className={styles.fieldValue}>
                    PI {issued.revision > 1 ? `Rev ${issued.revision} ` : ''}issued{order.sentAt ? ` ${formatMoment(order.sentAt)}` : ''}
                </span>
                <span className={styles.cardHint}>
                    {order.sentTo ? `Emailed to ${order.sentTo}` : 'No email was sent'}
                    {order.expiryDate ? ` · valid till ${formatDay(order.expiryDate)}` : ''}
                    {order.status === 'Submitted' ? ' · the lines have changed since, so a revised PI is due' : ''}
                </span>
            </div>
            <Button size="xs" variant={ButtonVariant.DEFAULT} onClick={() => void openSignedFile(() => getB2bOrderFileUrl(order.purchaseOrderId, issued.key))}>
                View PDF
            </Button>
        </div>
    ) : null;

    if (!editable) {
        return (
            <div className={styles.panelBody}>
                {issuedBanner}
                <LockedLines detail={detail} />
            </div>
        );
    }

    return (
        <div className={styles.panelBody}>
            {issuedBanner}
            {editable ? <span className={styles.cardTitle}>1. Check the order</span> : null}
            <SchemeStrip
                schemes={catalog?.schemes ?? []}
                lines={lines.filter((l) => l.quantity > 0).map((l) => ({ productId: l.productId, quantity: l.quantity, category: product.get(l.productId)?.category ?? null }))}
                entitlements={manualFree ? [] : entitlements}
                picks={free.filter((f) => f.productId)}
                emptyText="No scheme is running for this customer today."
            />
            {/* Its own scroll box (both ways): the panel around it does not scroll sideways, so a wide grid would be cut off. */}
            <DataSheet<ReviewRow> bare hideHeaderBar data={rows} columns={columns} getRowKey={(r) => r.key} maxHeight="60vh" emptyState="No paid lines." />
            {editable ? (
                <div className={styles.row}>
                    <Select size="xs" placeholder="Add a product" data={addOptions} value={addProduct} onChange={setAddProduct} searchable w={320} />
                    <Button
                        size="xs"
                        variant={ButtonVariant.SUBTLE}
                        leftSection={<PlusIcon size={14} />}
                        disabled={!addProduct}
                        onClick={() => {
                            if (!addProduct) return;
                            setLines((all) => [...all, { key: addProduct, productId: addProduct, quantity: 1 }]);
                            setAddProduct(null);
                        }}
                    >
                        Add line
                    </Button>
                </div>
            ) : null}

            <Card
                title="Free pieces"
                hint={
                    manualFree
                        ? 'Set by hand: the scheme entitlement is ignored and the note is kept on the review record.'
                        : earned > 0
                          ? 'Earned under running schemes and pre-filled in the Free column above. Tick Set by hand to give pieces outside the schemes.'
                          : 'This order earns no free pieces under the running schemes.'
                }
                actions={editable ? <Checkbox size="xs" label="Set by hand" checked={manualFree} onChange={(e) => setManualFree(e.currentTarget.checked)} /> : null}
            >
                {manualFree ? (
                    <div className={styles.stack}>
                        {free.map((pick, index) => (
                            <div key={index} className={styles.row}>
                                <Select
                                    size="xs"
                                    data={(catalog?.products ?? []).map((p) => ({ value: p.productId, label: `${p.name} (${p.sku})` }))}
                                    value={pick.productId || null}
                                    onChange={(v) => setFree((all) => all.map((p, i) => (i === index ? { ...p, productId: v ?? '' } : p)))}
                                    searchable
                                    w={320}
                                />
                                <NumberInput size="xs" min={0} allowDecimal={false} className={styles.qtyInput} value={pick.quantity} onChange={(v) => setFree((all) => all.map((p, i) => (i === index ? { ...p, quantity: Number(v) || 0 } : p)))} />
                                <ActionIcon variant="subtle" color="gray" aria-label="Remove" onClick={() => setFree((all) => all.filter((_, i) => i !== index))}>
                                    <TrashIcon size={16} />
                                </ActionIcon>
                            </div>
                        ))}
                        <Button size="xs" variant={ButtonVariant.SUBTLE} leftSection={<PlusIcon size={14} />} w="fit-content" onClick={() => setFree((all) => [...all, { productId: '', quantity: 1 }])}>
                            Add free product
                        </Button>
                        <TextInput size="xs" label="Why the free pieces differ" value={freeNote} onChange={(e) => setFreeNote(e.currentTarget.value)} />
                    </div>
                ) : null}
            </Card>

            {quote?.schemes.applied.length ? (
                <div className={styles.row}>
                    <span className={styles.cardHint}>Schemes applied:</span>
                    {quote.schemes.applied.map((s) => (
                        <Pill key={s.code} label={s.name} tone="positive" />
                    ))}
                </div>
            ) : null}
            {(quote?.errors ?? []).map((e) => (
                <span key={e} className={styles.error}>
                    {e}
                </span>
            ))}

            <Card title="Terms on the PI" hint="Copied from your standard terms (Settings → Brand Letterhead) when the order was placed. A change here applies to this order only.">
                <Textarea autosize minRows={3} maxRows={10} value={terms} placeholder="No terms on this order" onChange={(e) => setTerms(e.currentTarget.value)} />
            </Card>

            {editable ? (
                <>
                    <span className={styles.cardTitle}>{canIssue ? '2. The PI as the customer will get it' : `2. The PI as issued (Rev ${issued?.revision ?? 1})`}</span>
                    {preview ? (
                        <>
                            {canIssue ? (
                                <span className={styles.cardHint}>
                                    {dirty ? 'Showing your unsaved changes. ' : ''}
                                    {preview.to.length ? `Will be emailed to ${[...preview.to, ...preview.cc].join(', ')}` : 'The customer has no email on file, so it will only be saved here'}
                                    {` · valid till ${formatDay(preview.validTill)}`}
                                </span>
                            ) : null}
                            <iframe title="Proforma invoice preview" className={styles.piFrame} srcDoc={withPreviewMargin(preview.html)} sandbox="" />
                        </>
                    ) : (
                        <span className={styles.muted}>{previewError ?? (payload.paid.length ? 'Preparing the PI…' : 'Add a line to see the PI.')}</span>
                    )}
                    {error ? <span className={styles.error}>{error}</span> : null}
                    <div className={styles.row}>
                        {isAdmin && canIssue ? (
                            <Button
                                variant={ButtonVariant.ACTION}
                                loading={saving}
                                disabled={blocked || payload.paid.length === 0}
                                onClick={() => {
                                    setError(null);
                                    setSendOpen(true);
                                }}
                            >
                                {issueLabel}
                            </Button>
                        ) : null}
                        {dirty ? (
                            <Button variant={ButtonVariant.DEFAULT} loading={saving} disabled={blocked} onClick={() => void act(saveLines)}>
                                Save changes only
                            </Button>
                        ) : null}
                    </div>
                    {!isAdmin && canIssue ? <span className={styles.muted}>Only a brand admin can issue the PI.</span> : null}
                    {issued && !dirty && order.status !== 'Submitted' ? (
                        <span className={styles.muted}>To revise the PI, change the lines above; the button to issue Rev {nextRevision} appears here.</span>
                    ) : null}
                </>
            ) : null}
            {sendOpen && preview ? (
                <SendPiModal
                    orderId={order.purchaseOrderId}
                    customerName={customerName}
                    revision={preview.revision}
                    validTill={preview.validTill}
                    summary={{
                        skus: quote?.lines.length ?? 0,
                        units: [...(quote?.lines ?? []), ...(quote?.freeLines ?? [])].reduce((sum, l) => sum + l.quantity, 0),
                        payable: quote?.totals.payable ?? order.total,
                    }}
                    defaults={{ to: preview.to, cc: preview.cc }}
                    savesChanges={dirty}
                    sending={saving}
                    error={error}
                    onClose={() => setSendOpen(false)}
                    onSend={(send) =>
                        void act(async () => {
                            if (dirty) await saveLines();
                            await issueB2bPi(order.purchaseOrderId, send);
                            setSendOpen(false);
                        })
                    }
                />
            ) : null}
        </div>
    );
};

export default ReviewTab;
